import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Transaction } from "@/types/payments";
import { TrendingUp, Receipt, ArrowLeftRight, DollarSign, Lock } from "lucide-react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { useFormatters } from "@financialharmony/ui";
import { TransactionService } from "@/services/transactionService";

const transactionService = new TransactionService();

interface MarkAsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  transaction: Transaction | null;
  formatCurrency: (amount: number, currency: string) => string;
}

interface CategoryOption {
  id: string;
  labelKey: "interest" | "tax" | "transfer" | "bankFee";
  icon: React.ReactNode;
}

const categoryOptions: CategoryOption[] = [
  { id: "interest", labelKey: "interest", icon: <TrendingUp className="h-5 w-5 text-green-600" /> },
  { id: "tax", labelKey: "tax", icon: <Receipt className="h-5 w-5 text-blue-600" /> },
  { id: "transfer", labelKey: "transfer", icon: <ArrowLeftRight className="h-5 w-5 text-purple-600" /> },
  { id: "bank-fee", labelKey: "bankFee", icon: <DollarSign className="h-5 w-5 text-orange-600" /> },
];

// FIN-1567 PR5 stub message -- the real booking (FIN-1575) doesn't exist yet, but the request is
// logged server-side (PRIVATE_MARK_REQUESTED) so demand has real evidence.
const NOT_AVAILABLE_YET_MESSAGE = "Marking private isn't available yet. We've been notified.";

const MarkAsModal = ({
  open,
  setOpen,
  transaction,
  formatCurrency
}: MarkAsModalProps) => {
  const t = useTranslations("consultantBank.markAs");
  const { formatDate } = useFormatters();
  const [markingPrivate, setMarkingPrivate] = useState(false);
  const isPersonalAccount = transaction?.account?.accountType === "personal";

  const handleClose = () => {
    setOpen(false);
  };

  const handleMarkPrivate = async () => {
    if (!transaction?.bankTransactionId || markingPrivate) return;
    setMarkingPrivate(true);
    try {
      await transactionService.markTransactionsPrivate([transaction.bankTransactionId], "mark");
      toast(NOT_AVAILABLE_YET_MESSAGE);
    } catch {
      // handleError (in TransactionApiClient) already surfaced this failure; nothing further to do.
    } finally {
      setMarkingPrivate(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>
            {transaction ? (
              <div className="text-sm mt-2">
                <p><strong>{t("transaction")}</strong> {transaction.description}</p>
                <p><strong>{t("amount")}</strong> {formatCurrency(transaction.amount, transaction.currency)}</p>
                <p><strong>{t("date")}</strong> {formatDate(transaction.date, "numeric")}</p>
              </div>
            ) : (
              <p>{t("selectCategory")}</p>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {isPersonalAccount && (
            <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-center space-x-3">
                <Lock className="h-5 w-5 text-slate-600" />
                <span className="font-medium">{t("private")}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={markingPrivate}
                onClick={handleMarkPrivate}
              >
                {t("markPrivate")}
              </Button>
            </div>
          )}
          {categoryOptions.map((category) => (
            <div
              key={category.id}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                {category.icon}
                <span className="font-medium">{t(`categories.${category.labelKey}`)}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled
                className="text-muted-foreground"
              >
                {t("comingSoon")}
              </Button>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarkAsModal;
